import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { api } from '../services/api';

function setSentryUser(userData) {
  if (!userData) { Sentry.setUser(null); return; }
  Sentry.setUser({ id: String(userData.id), username: userData.email, role: userData.role });
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('crm_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Ao iniciar com sessão existente, busca dados frescos (esn_nome, must_change_password)
  useEffect(() => {
    const stored = localStorage.getItem('crm_user');
    if (!stored) return;
    // Associa usuário ao Sentry imediatamente com dados locais; atualiza após /me
    try { setSentryUser(JSON.parse(stored)); } catch { /* ignore */ }
    api.getMe()
      .then(fresh => {
        if (!fresh) return; // resposta vazia — o 401 agora rejeita e cai no catch abaixo
        setUser(prev => {
          if (!prev) return prev;
          const updated = { ...prev, esn_nome: fresh.esn_nome, must_change_password: fresh.must_change_password };
          localStorage.setItem('crm_user', JSON.stringify(updated));
          setSentryUser(updated);
          return updated;
        });
      })
      .catch(() => {}); // falha silenciosa — JWT pode estar expirado, ProtectedRoute cuida do redirect
  }, []);

  const login = useCallback((token, userData) => {
    localStorage.setItem('crm_token', token);
    localStorage.setItem('crm_user', JSON.stringify(userData));
    setUser(userData);
    setSentryUser(userData);
    // esn_nome não está no JWT payload — busca via /me para enriquecer o contexto
    api.getMe()
      .then(fresh => {
        if (!fresh) return; // resposta vazia — userData já está setado; o 401 cai no catch
        const updated = { ...userData, esn_nome: fresh.esn_nome, must_change_password: fresh.must_change_password };
        localStorage.setItem('crm_user', JSON.stringify(updated));
        setUser(updated);
        setSentryUser(updated);
      })
      .catch(() => {});
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    setUser(null);
    setSentryUser(null);
  }, []);

  const refreshUser = useCallback(() => {
    return api.getMe().then(fresh => {
      const updated = { ...user, ...fresh };
      localStorage.setItem('crm_user', JSON.stringify(updated));
      setUser(updated);
      return updated;
    });
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
